import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BuildingOfficeIcon,
  ArrowPathIcon,
  PhotoIcon,
  TrashIcon,
  CloudIcon,
  GlobeAltIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import ExploradorModelosAdmin from '../components/analiseCompra/explorador/ExploradorModelosAdmin';
import { organizacaoApi, type Organizacao } from '../api/organizacao';
import { useToast } from '../components/ui/Toast';
import { maskCnpjInput, maskTelefoneInput, unmaskDigits } from '../utils/format';
import {
  PainelIris,
  AbasSublinhado,
  BadgeEmBreve,
  inputIris,
  btnPrimario,
  btnSecundario,
  btnDanger,
} from '../components/ui/iris';
import {
  useCloudflareConfig,
  useCloudflareZones,
  useSaveCredentials,
  useCloudflareSetup,
  useServiceAction,
  useTeardown,
} from '../hooks/useCloudflare';

// Parâmetros do sistema (CT-e, BI, Reposição) saíram desta tela — edição
// centralizada em /config (Configurações). Organização = identidade + infra.
type Aba = 'cadastro' | 'cloudflare' | 'modelos';

/** Handler de salvar registrado pela aba ativa — header dispara. */
type SalvarHandler = (() => Promise<void>) | null;

const OrganizacaoPage: React.FC = () => {
  const [aba, setAba] = useState<Aba>('cadastro');
  const [salvando, setSalvando] = useState(false);
  const salvarRef = useRef<SalvarHandler>(null);

  const registrarSalvar = useCallback((fn: SalvarHandler) => {
    salvarRef.current = fn;
  }, []);

  const salvarHeader = async () => {
    if (!salvarRef.current) return;
    setSalvando(true);
    try { await salvarRef.current(); }
    finally { setSalvando(false); }
  };

  return (
    // Mock Íris: conteúdo de Organização limitado a 880px centralizado.
    <div className="max-w-[980px] mx-auto space-y-4">
      {/* Header (mock: sem ícone — título 22px + descrição 13.5px) */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-[22px] font-bold text-tx tracking-[-0.02em]">Organização</h1>
          <p className="text-[13.5px] text-tx-3 mt-0.5">Dados da empresa e acesso externo (Cloudflare).</p>
        </div>
        {aba === 'cadastro' && (
          <button onClick={salvarHeader} disabled={salvando} className={btnPrimario}>
            {salvando && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
            {salvando ? 'Salvando…' : 'Salvar alterações'}
          </button>
        )}
      </div>

      <AbasSublinhado<Aba>
        abas={[
          { key: 'cadastro', label: 'Cadastro', icon: <BuildingOfficeIcon className="w-4 h-4" /> },
          { key: 'cloudflare', label: 'Cloudflare', icon: <CloudIcon className="w-4 h-4" /> },
          { key: 'modelos', label: 'Modelos de dados', icon: <TableCellsIcon className="w-4 h-4" /> },
        ]}
        ativa={aba}
        onChange={setAba}
      />

      {aba === 'cadastro' && <AbaCadastro registrarSalvar={registrarSalvar} />}
      {aba === 'cloudflare' && <AbaCloudflare />}
      {aba === 'modelos' && <ExploradorModelosAdmin />}
    </div>
  );
};

// ── Aba Cadastro ─────────────────────────────────────────────────────────

const AbaCadastro: React.FC<{ registrarSalvar: (fn: SalvarHandler) => void }> = ({ registrarSalvar }) => {
  const toast = useToast();
  const [org, setOrg] = useState<Organizacao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const fileRef = useRef<HTMLInputElement>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      // Backend grava só dígitos; aplica máscara pro display imediato.
      // Sem isso, valor antigo apareceria cru até o user clicar no input.
      const data = await organizacaoApi.obter();
      setOrg({
        ...data,
        cnpj: maskCnpjInput(data.cnpj ?? ''),
        telefone: maskTelefoneInput(data.telefone ?? ''),
        celular: maskTelefoneInput(data.celular ?? ''),
      });
    } catch { /* first time */ }
    finally { setCarregando(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = useCallback(async () => {
    if (!org) return;
    try {
      // Strip de máscara antes de enviar — backend grava só dígitos pra
      // normalizar entre clientes. UI volta a formatar no próximo load.
      const payload: Organizacao = {
        ...org,
        cnpj: unmaskDigits(org.cnpj),
        telefone: unmaskDigits(org.telefone),
        celular: unmaskDigits(org.celular),
      };
      await organizacaoApi.salvar(payload);
      toast.sucesso('Dados salvos com sucesso!');
    } catch { toast.erro('Erro ao salvar os dados da organização'); }
  }, [org, toast]);

  useEffect(() => {
    registrarSalvar(salvar);
    return () => registrarSalvar(null);
  }, [registrarSalvar, salvar]);

  const handleLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.aviso('Máximo 2MB'); return; }
    try {
      const base64 = await organizacaoApi.uploadLogo(file);
      setOrg(prev => prev ? { ...prev, logoBase64: base64 } : prev);
      toast.sucesso('Logo atualizado!');
    } catch { toast.erro('Erro ao enviar logo'); }
  };

  const removerLogo = () => {
    setOrg(prev => prev ? { ...prev, logoBase64: undefined } : prev);
  };

  if (carregando) return <CarregandoIris />;
  if (!org) return null;

  const nomeExibicao = org.nomeFantasia || org.razaoSocial || 'Organização';
  const inicial = nomeExibicao.trim().charAt(0).toUpperCase() || 'U';

  return (
    <div className="space-y-4">
      {/* Hero de identidade */}
      <PainelIris>
        <div className="flex items-center gap-4 flex-wrap">
          {org.logoBase64 ? (
            <img
              src={`data:image/png;base64,${org.logoBase64}`}
              alt="Logo"
              className="h-16 w-16 object-contain rounded-iris-lg border border-line bg-white p-1"
            />
          ) : (
            <div className="h-16 w-16 rounded-iris-lg bg-accent text-white flex items-center justify-center text-2xl font-bold select-none">
              {inicial}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-lg font-bold text-tx tracking-[-0.02em] truncate">{nomeExibicao}</div>
            <div className="text-[13px] text-tx-2 truncate">
              {org.razaoSocial || '—'}
              {org.cnpj ? <> · <span className="font-mono">{org.cnpj}</span></> : null}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogo} className="hidden" />
            <button onClick={() => fileRef.current?.click()} className={`${btnSecundario} h-8 px-3 text-xs`}>
              <PhotoIcon className="w-3.5 h-3.5" /> {org.logoBase64 ? 'Trocar logo' : 'Enviar logo'}
            </button>
            {org.logoBase64 && (
              <button
                onClick={removerLogo}
                className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-iris text-danger hover:bg-danger-soft transition-colors"
              >
                <TrashIcon className="w-3.5 h-3.5" /> Remover
              </button>
            )}
          </div>
          <p className="text-[11px] text-tx-3 w-full sm:w-auto sm:ml-auto">PNG ou JPG, máx 2MB</p>
        </div>
      </PainelIris>

      {/* Dados cadastrais */}
      <PainelIris titulo="Dados cadastrais" descricao="Identificação fiscal da empresa.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Field label="Razão Social" value={org.razaoSocial ?? ''} onChange={v => setOrg({ ...org, razaoSocial: v })} />
          </div>
          <Field label="Nome Fantasia" value={org.nomeFantasia ?? ''} onChange={v => setOrg({ ...org, nomeFantasia: v })} />
          <Field label="CNPJ" value={org.cnpj ?? ''} onChange={v => setOrg({ ...org, cnpj: v })} placeholder="00.000.000/0000-00" mask={maskCnpjInput} inputMode="numeric" />
          <Field label="Inscrição Estadual" value={org.inscricaoEstadual ?? ''} onChange={v => setOrg({ ...org, inscricaoEstadual: v })} />
          {/* TODO(backend): regime tributário não existe no model Organizacao — SISTEMA_PLANEJAMENTO.md */}
          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-tx-2 mb-1">
              Regime tributário <BadgeEmBreve />
            </label>
            <input type="text" disabled placeholder="Simples Nacional / Lucro Presumido / Lucro Real" className={`w-full ${inputIris}`} />
          </div>
        </div>
      </PainelIris>

      {/* Contato */}
      <PainelIris titulo="Contato" descricao="Canais usados em documentos e no catálogo.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Telefone" value={org.telefone ?? ''} onChange={v => setOrg({ ...org, telefone: v })} placeholder="(00) 0000-0000" mask={maskTelefoneInput} inputMode="tel" />
          <Field label="Celular" value={org.celular ?? ''} onChange={v => setOrg({ ...org, celular: v })} placeholder="(00) 00000-0000" mask={maskTelefoneInput} inputMode="tel" />
          <Field label="Email" value={org.email ?? ''} onChange={v => setOrg({ ...org, email: v })} />
          <Field label="Site" value={org.site ?? ''} onChange={v => setOrg({ ...org, site: v })} placeholder="https://" />
        </div>
      </PainelIris>

      {/* Endereço (mock: grid 3 col, logradouro linha inteira, UF/CEP células próprias) */}
      <PainelIris titulo="Endereço" descricao="Localização da sede.">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-3">
            <Field label="Logradouro" value={org.endereco ?? ''} onChange={v => setOrg({ ...org, endereco: v })} />
          </div>
          <Field label="Número" value={org.numero ?? ''} onChange={v => setOrg({ ...org, numero: v })} />
          <Field label="Bairro" value={org.bairro ?? ''} onChange={v => setOrg({ ...org, bairro: v })} />
          <Field label="Cidade" value={org.cidade ?? ''} onChange={v => setOrg({ ...org, cidade: v })} />
          <Field label="UF" value={org.estado ?? ''} onChange={v => setOrg({ ...org, estado: v.toUpperCase().slice(0, 2) })} />
          <Field label="CEP" value={org.cep ?? ''} onChange={v => setOrg({ ...org, cep: v })} />
        </div>
      </PainelIris>
    </div>
  );
};

const CarregandoIris: React.FC = () => (
  <div className="p-6 flex items-center justify-center">
    <ArrowPathIcon className="w-5 h-5 animate-spin text-tx-3 mr-2" />
    <span className="text-tx-3">Carregando...</span>
  </div>
);

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Máscara aplicada no onChange — `maskCnpjInput`, `maskTelefoneInput`, etc. */
  mask?: (v: string) => string;
  inputMode?: 'text' | 'tel' | 'numeric';
}> = ({ label, value, onChange, placeholder, mask, inputMode }) => (
  <div>
    <label className="block text-xs font-medium text-tx-2 mb-1">{label}</label>
    <input
      type="text"
      inputMode={inputMode}
      value={value}
      onChange={e => onChange(mask ? mask(e.target.value) : e.target.value)}
      placeholder={placeholder}
      className={`w-full ${inputIris}`}
    />
  </div>
);

// ── Aba Cloudflare ────────────────────────────────────────────────────────

const AbaCloudflare: React.FC = () => {
  const { data: cfg, isLoading } = useCloudflareConfig();
  const saveCredentials = useSaveCredentials();
  const setup = useCloudflareSetup();
  const teardown = useTeardown();
  const serviceAction = useServiceAction();
  const { data: zones } = useCloudflareZones(!!cfg?.credentialsConfigured);

  const [apiToken, setApiToken] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [subdomain, setSubdomain] = useState('hub');
  const [tunnelName, setTunnelName] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);

  if (isLoading) {
    return <div className="text-sm text-tx-3">Carregando…</div>;
  }

  const handleSaveCredentials = async () => {
    setErro(null);
    try {
      await saveCredentials.mutateAsync(apiToken);
      setApiToken('');
      setSucesso('Token validado e salvo.');
      setTimeout(() => setSucesso(null), 3000);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setErro(err.response?.data?.error || 'Erro ao validar token');
    }
  };

  const handleSetup = async () => {
    setErro(null);
    setSucesso(null);
    try {
      const r = await setup.mutateAsync({ zoneId, subdomain, tunnelName: tunnelName || undefined });
      setSucesso(`Tunnel criado! Hostname: ${r.hostname}`);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setErro(err.response?.data?.error || 'Erro ao criar tunnel');
    }
  };

  const handleTeardown = async () => {
    if (!confirm('Tem certeza? O tunnel será removido da Cloudflare, o DNS apagado e o serviço local desinstalado.'))
      return;
    setErro(null);
    try {
      await teardown.mutateAsync();
      setSucesso('Tunnel removido com sucesso.');
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setErro(err.response?.data?.error || 'Erro ao remover tunnel');
    }
  };

  const zoneSel = (zones ?? []).find(z => z.id === zoneId);
  const hostnamePreview = zoneSel && subdomain ? `${subdomain}.${zoneSel.name}` : '';

  return (
    <div className="space-y-4">
      {/* Aviso binário */}
      {!cfg?.binarioInstalado && (
        <div className="rounded-iris border border-warning-border bg-warning-soft px-3 py-2 text-xs text-warning">
          <strong>cloudflared.exe não encontrado</strong> em <code>{cfg?.binarioPath}</code>. Reinstale o UtilyHub
          ou copie o binário manualmente antes de tentar criar o tunnel.
        </div>
      )}

      {/* Status atual (mock: quadrado de ícone 40px + hostname mono + chip Conectado) */}
      {cfg?.hostname && (
        <section className="flex items-center gap-3.5 rounded-iris-lg border border-ok-border bg-ok-soft p-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-iris bg-ok text-white">
            <GlobeAltIcon className="w-5 h-5" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-mono font-semibold text-tx truncate">{cfg.hostname}</div>
            <div className="mt-0.5 text-[12.5px] text-tx-2">
              Tunnel ativo · serviço local: <ServiceBadge status={cfg.serviceStatus} /> · <span className="font-mono">{cfg.tunnelName}</span>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ok shrink-0">
            <span className="h-[7px] w-[7px] rounded-full bg-ok" /> Conectado
          </span>
        </section>
      )}

      {/* Mensagens */}
      {erro && <div className="rounded-iris border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger">{erro}</div>}
      {sucesso && <div className="rounded-iris border border-ok-border bg-ok-soft px-3 py-2 text-xs text-ok">{sucesso}</div>}

      {/* Credenciais */}
      <PainelIris titulo="Credenciais Cloudflare" descricao="API Token com Tunnel:Edit + DNS:Edit + Zone:Read.">
        <p className="text-[13px] text-tx-3 mb-3">
          Crie um API Token em{' '}
          <a href="https://dash.cloudflare.com/profile/api-tokens" target="_blank" rel="noreferrer" className="text-accent-text underline">
            dash.cloudflare.com/profile/api-tokens
          </a>{' '}
          com permissões: <em>Account / Cloudflare Tunnel:Edit</em> + <em>Zone / DNS:Edit</em> + <em>Zone:Read</em>.
        </p>

        {cfg?.credentialsConfigured && (
          <div className="text-xs text-tx-2 mb-3">
            Conta: <strong>{cfg.accountName}</strong> ({cfg.accountId}) · Token: <span className="font-mono">{cfg.apiTokenMasked}</span>
          </div>
        )}

        <div className="flex gap-2">
          <input
            type="password"
            value={apiToken}
            onChange={e => setApiToken(e.target.value)}
            placeholder={cfg?.credentialsConfigured ? 'Trocar token...' : 'Cole o API Token aqui'}
            className={`flex-1 font-mono ${inputIris}`}
          />
          <button
            type="button"
            disabled={!apiToken || saveCredentials.isPending}
            onClick={handleSaveCredentials}
            className={btnPrimario}
          >
            {saveCredentials.isPending ? 'Validando…' : 'Validar e salvar'}
          </button>
        </div>
      </PainelIris>

      {/* Setup do tunnel */}
      {cfg?.credentialsConfigured && !cfg.tunnelId && (
        <PainelIris titulo="Configurar novo tunnel">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-tx-2 mb-1">Domínio (zone)</label>
              <select
                value={zoneId}
                onChange={e => setZoneId(e.target.value)}
                className={`w-full ${inputIris}`}
              >
                <option value="">— selecione —</option>
                {(zones ?? []).map(z => (
                  <option key={z.id} value={z.id}>{z.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-tx-2 mb-1">Subdomínio</label>
                <input
                  type="text"
                  value={subdomain}
                  onChange={e => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="hub"
                  className={`w-full font-mono ${inputIris}`}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-tx-2 mb-1">Nome do tunnel (opcional)</label>
                <input
                  type="text"
                  value={tunnelName}
                  onChange={e => setTunnelName(e.target.value)}
                  placeholder={hostnamePreview ? `utilyhub-${subdomain}-${zoneSel?.name}` : 'auto'}
                  className={`w-full font-mono ${inputIris}`}
                />
              </div>
            </div>

            {hostnamePreview && (
              <div className="text-xs text-tx-2">
                Hostname final: <strong className="font-mono">{hostnamePreview}</strong> →{' '}
                <span className="font-mono">{cfg.localTarget}</span>
              </div>
            )}

            <button
              type="button"
              disabled={!zoneId || !subdomain || setup.isPending || !cfg.binarioInstalado}
              onClick={handleSetup}
              className={btnPrimario}
            >
              {setup.isPending ? 'Configurando… (5 passos)' : 'Criar tunnel e iniciar serviço'}
            </button>
          </div>
        </PainelIris>
      )}

      {/* Service ações */}
      {cfg?.tunnelId && (
        <PainelIris titulo="Serviço local" descricao="Controle do agente cloudflared na máquina.">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={serviceAction.isPending}
              onClick={() => serviceAction.mutate('start')}
              className={`${btnPrimario} h-8 px-3 text-xs`}
            >
              Iniciar
            </button>
            <button
              type="button"
              disabled={serviceAction.isPending}
              onClick={() => serviceAction.mutate('stop')}
              className={`${btnSecundario} h-8 px-3 text-xs`}
            >
              Parar
            </button>
            <button
              type="button"
              disabled={serviceAction.isPending}
              onClick={() => serviceAction.mutate('reinstall')}
              className={`${btnSecundario} h-8 px-3 text-xs`}
            >
              Reinstalar
            </button>
          </div>
        </PainelIris>
      )}

      {/* Zona de perigo */}
      {cfg?.tunnelId && (
        <section className="rounded-iris-lg border border-danger-border bg-danger-soft p-4">
          <h2 className="text-[15px] font-semibold text-danger mb-1">Zona de perigo</h2>
          <p className="text-[13px] text-tx-2 mb-3">
            Apaga o tunnel da Cloudflare, remove o DNS e desinstala o serviço local.
          </p>
          <button
            type="button"
            disabled={teardown.isPending}
            onClick={handleTeardown}
            className={`${btnDanger} h-8 px-3 text-xs`}
          >
            {teardown.isPending ? 'Removendo…' : 'Remover tunnel'}
          </button>
        </section>
      )}
    </div>
  );
};

const ServiceBadge: React.FC<{ status: string | null | undefined }> = ({ status }) => {
  const cores: Record<string, string> = {
    running: 'bg-ok-soft text-ok',
    stopped: 'bg-warning-soft text-warning',
    not_installed: 'bg-surface-2 text-tx-2',
    error: 'bg-danger-soft text-danger',
  };
  const label = status === 'running' ? 'rodando' : status === 'stopped' ? 'parado' : status === 'not_installed' ? 'não instalado' : status || 'desconhecido';
  const cls = cores[status || ''] || cores.not_installed;
  return <span className={`inline-block px-1.5 py-0.5 rounded-iris-sm text-[10px] font-medium ${cls}`}>{label}</span>;
};

export default OrganizacaoPage;
